/** Popup presentation reads the clicked DOM hooks; it does not fetch or read the entity store. */
import { oddsToDecimal, decimalToOdds, normalizeDisplayOddsFormat, ODDS_FORMAT_LABELS } from '../../shared/odds-format';
import { calculateOddsPayout } from '../../shared/odds-payout';
const markets = [
  { prefix: 'eu', sides: ['home', 'draw', 'away'], label: '1X2', period: 'FT', line: '' },
  { prefix: 'asia-ft', sides: ['home', 'away'], label: 'Cược chấp', period: 'FT', line: 'asia-ft-handicap' },
  { prefix: 'bs', sides: ['over', 'under'], label: 'Tài xỉu', period: 'FT', line: 'bs-line' },
  { prefix: 'cr', sides: ['over', 'under'], label: 'Tài xỉu phạt góc', period: 'FT', line: 'cr-line' },
  { prefix: 'asia-ht', sides: ['home', 'away'], label: 'Cược chấp', period: 'HT', line: 'asia-ht-handicap' },
  { prefix: 'bs-ht', sides: ['over', 'under'], label: 'Tài xỉu', period: 'HT', line: 'bs-ht-line' },
];
const quotes = markets.flatMap((market) => market.sides.map((side) => ({ ...market, side, hook: `data-gwd-odds-${market.prefix}-${side}` })));
const selector = quotes.map(({ hook }) => `[${hook}]`).join(',');
const labels: Record<string, string> = { home: 'Chủ', away: 'Khách', draw: 'Hòa', over: 'Tài', under: 'Xỉu' };
const popupSyncers = new WeakMap<HTMLElement, () => void>();
export const syncBetPopup = (root: HTMLElement) => popupSyncers.get(root)?.();
const text = (root: Element, hook: string): string => root.querySelector(`[${hook}]`)?.textContent?.trim() || '';
const priceText = (node: HTMLElement): string => {
  const copy = node.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('.gwd-odds-board__trend, [aria-hidden="true"]').forEach((icon) => icon.remove());
  return copy.textContent?.trim() || '';
};
const validPrice = (node: HTMLElement) => {
  return oddsToDecimal(node.dataset.gwdOddsDecimal, 'decimal') !== null
    && !node.closest('[hidden], .is-locked');
};

const mountPopupDrag = (popup: HTMLDialogElement, getAnchor: () => HTMLElement | undefined) => {
  const handle = popup.querySelector<HTMLElement>('.gwd-bet-popup__header')!;
  let position: { x: number; y: number } | undefined;
  let anchored = true;
  let drag: { id: number; x: number; y: number; left: number; top: number } | undefined;
  handle.dataset.gwdBetDrag = '';
  handle.tabIndex = 0;
  handle.setAttribute('aria-label', 'Di chuyển popup: kéo tiêu đề hoặc dùng phím mũi tên');
  const place = (x: number, y: number) => {
    const bounds = popup.getBoundingClientRect();
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0;
    const top = viewport?.offsetTop || 0;
    const width = viewport?.width || document.documentElement.clientWidth;
    const height = viewport?.height || window.innerHeight;
    position = {
      x: Math.max(left, Math.min(x, left + Math.max(0, width - bounds.width))),
      y: Math.max(top, Math.min(y, top + Math.max(0, height - bounds.height))),
    };
    popup.style.position = 'fixed'; popup.style.margin = '0';
    popup.style.right = 'auto'; popup.style.bottom = 'auto';
    popup.style.left = `${position.x}px`; popup.style.top = `${position.y}px`;
  };
  const clamp = () => {
    if (!popup.open) return;
    const anchor = getAnchor();
    if (anchored && anchor?.isConnected) {
      const target = anchor.getBoundingClientRect();
      const bounds = popup.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const gap = 10;
      const below = bottom - target.bottom - gap;
      const above = target.top - top - gap;
      const useAbove = below < bounds.height && above > below;
      place(target.left + target.width / 2 - 24, useAbove ? target.top - bounds.height - gap : target.bottom + gap);
      const placed = popup.getBoundingClientRect();
      const detached = useAbove ? placed.bottom > target.top : placed.top < target.bottom;
      const tip = target.left + target.width / 2 - placed.left;
      popup.dataset.gwdBetPlacement = detached || tip < 10 || tip > placed.width - 10 ? 'none' : useAbove ? 'above' : 'below';
      popup.style.setProperty('--gwd-bet-arrow-x', `${Math.max(12, Math.min(tip, placed.width - 12))}px`);
    } else if (position) {
      popup.dataset.gwdBetPlacement = 'none';
      place(position.x, position.y);
    }
  };
  const end = () => {
    const pointer = drag?.id; drag = undefined;
    popup.classList.remove('is-dragging');
    if (pointer !== undefined && handle.hasPointerCapture(pointer)) handle.releasePointerCapture(pointer);
  };
  handle.addEventListener('pointerdown', (event) => {
    if (!event.isPrimary || event.button !== 0 || (event.target instanceof Element && event.target.closest('button, input, select, a'))) return;
    const bounds = popup.getBoundingClientRect();
    drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: bounds.left, top: bounds.top };
    handle.setPointerCapture(event.pointerId);
    popup.classList.add('is-dragging'); handle.focus({ preventScroll: true });
    event.preventDefault();
  });
  handle.addEventListener('pointermove', (event) => {
    if (drag?.id !== event.pointerId) return;
    anchored = false; popup.dataset.gwdBetPlacement = 'none';
    place(drag.left + event.clientX - drag.x, drag.top + event.clientY - drag.y);
  });
  handle.addEventListener('pointerup', (event) => { if (drag?.id === event.pointerId) end(); });
  handle.addEventListener('pointercancel', end);
  handle.addEventListener('lostpointercapture', end);
  handle.addEventListener('keydown', (event) => {
    if (event.target !== handle) return;
    const delta: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const direction = delta[event.key];
    if (!direction) return;
    event.preventDefault();
    const bounds = popup.getBoundingClientRect(); const step = event.shiftKey ? 30 : 10;
    anchored = false; popup.dataset.gwdBetPlacement = 'none';
    place(bounds.left + direction[0] * step, bounds.top + direction[1] * step);
  });
  const observer = new ResizeObserver(clamp);
  popup.addEventListener('close', () => {
    end(); observer.disconnect(); window.removeEventListener('resize', clamp);
    window.removeEventListener('scroll', clamp, true);
    window.visualViewport?.removeEventListener('resize', clamp);
    window.visualViewport?.removeEventListener('scroll', clamp);
  });
  return () => {
    anchored = true;
    clamp(); observer.observe(popup); window.addEventListener('resize', clamp);
    window.addEventListener('scroll', clamp, true);
    window.visualViewport?.addEventListener('resize', clamp);
    window.visualViewport?.addEventListener('scroll', clamp);
  };
};

export const prepareBetTriggers = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>(selector).forEach((node) => {
    const enabled = validPrice(node);
    const attributes = {
      'data-gwd-bet-trigger': '', role: 'button', tabindex: enabled ? '0' : '-1',
      'aria-disabled': String(!enabled), 'aria-haspopup': 'dialog',
      'aria-label': `Xem thông tin cược ${priceText(node)}`,
    };
    Object.entries(attributes).forEach(([name, value]) => {
      if (node.getAttribute(name) !== value) node.setAttribute(name, value);
    });
  });
};

const stakeDigits = (value: string) => value.replace(/\D/g, '');
const formatStake = (value: string) => stakeDigits(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const formatStakeInput = (input: HTMLInputElement) => {
  const value = input.value;
  const start = stakeDigits(value.slice(0, input.selectionStart ?? value.length)).length;
  const end = stakeDigits(value.slice(0, input.selectionEnd ?? value.length)).length;
  const direction = input.selectionDirection ?? 'none';
  input.value = formatStake(value);
  const position = (digits: number) => {
    let index = 0;
    while (index < input.value.length && digits > 0) {
      if (/\d/.test(input.value[index])) digits--;
      index++;
    }
    return index;
  };
  input.setSelectionRange(position(start), position(end), direction);
};

export const mountBetPopup = (root: HTMLElement) => {
  let dialog: HTMLDialogElement | undefined;
  let trigger: HTMLElement | undefined;
  let selection: Record<string, string> = {};
  let available = false;
  let selectedHook = '';
  let resumeDrag: (() => void) | undefined;
  const money = (value: number) => `${value.toLocaleString('vi-VN')} ₫`;
  const fill = (name: string, value: string) => {
    dialog?.querySelectorAll<HTMLElement>(`[data-gwd-bet-${name}]`).forEach((node) => { node.textContent = value; });
  };
  const notifyAmount = () => {
    const input = dialog!.querySelector<HTMLInputElement>('[data-gwd-bet-stake]')!;
    const amount = Number(stakeDigits(input.value));
    const valid = input.value.trim() !== '' && Number.isSafeInteger(amount) && amount > 0;
    input.setAttribute('aria-invalid', String(!valid));
    const format = normalizeDisplayOddsFormat(selection.format);
    const quote = decimalToOdds(selection.decimal, format);
    const targetProfit = (format === 'malay' || format === 'indo') && quote !== null && quote < 0;
    fill('input-label', targetProfit ? 'Tiền thắng mục tiêu (₫)' : 'Số tiền cược (₫)');
    fill('input-hint', targetProfit ? 'Số nhập là tiền thắng; tiền chịu rủi ro được tính bên dưới.' : 'Số nhập là số tiền chịu rủi ro.');
    const result = valid && available ? calculateOddsPayout(amount, selection.decimal, format) : null;
    const risk = result?.risk ?? null;
    const profit = result?.profit ?? null;
    const totalReturn = result?.totalReturn ?? null;
    fill('stake-total', risk === null ? '—' : money(risk));
    fill('profit', profit === null ? '—' : money(profit));
    fill('return', totalReturn === null ? '—' : money(totalReturn));
    dialog!.querySelectorAll<HTMLElement>('[data-gwd-bet-amount]').forEach((button) => {
      button.setAttribute('aria-pressed', String(valid && Number(button.dataset.gwdBetAmount) === amount));
    });
    root.dispatchEvent(new CustomEvent('gwd:bet-change', {
      bubbles: true, detail: { ...selection, available, stake: valid ? amount : null, inputAmount: valid ? amount : null, inputMode: targetProfit ? 'profit' : 'risk', risk, profit, totalReturn, popup: dialog },
    }));
  };
  const ensureDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'gwd-bet-popup'; dialog.dataset.gwdBetPopup = '';
    dialog.setAttribute('aria-label', 'Thông tin cược');
    dialog.innerHTML = `<div class="gwd-bet-popup__surface"><header class="gwd-bet-popup__header"><div class="gwd-bet-popup__heading"><span aria-hidden="true">⚽</span><span class="gwd-bet-popup__live" data-gwd-bet-live hidden>TRỰC TIẾP</span><h2>Bóng đá / <span data-gwd-bet-market></span></h2></div><button type="button" data-gwd-bet-collapse aria-expanded="true" aria-label="Thu gọn">⌃</button><button type="button" data-gwd-bet-close aria-label="Đóng">×</button></header>
      <div data-gwd-bet-body>
        <p data-gwd-bet-status role="status" hidden></p>
        <div class="gwd-bet-popup__pick"><i class="fa-solid fa-coins gwd-bet-popup__coin" aria-hidden="true"></i><div class="gwd-bet-popup__selection" data-gwd-bet-selection></div><div class="gwd-bet-popup__quote"><span data-gwd-bet-line></span><span class="gwd-bet-popup__score">[<span data-gwd-bet-vs hidden>VS</span><span data-gwd-bet-home-score></span><span data-gwd-bet-score-separator> - </span><span data-gwd-bet-away-score></span>]</span><strong data-gwd-bet-price></strong><small data-gwd-bet-period></small></div><p class="gwd-bet-popup__company"><span data-gwd-bet-company></span> · <span data-gwd-bet-format-label></span></p></div>
        <label class="gwd-bet-popup__stake"><span data-gwd-bet-input-label>Số tiền cược (₫)</span><span class="gwd-bet-popup__input-wrap"><input type="text" inputmode="numeric" autocomplete="off" value="100.000" data-gwd-bet-stake><button type="button" data-gwd-bet-clear aria-label="Xóa số tiền">×</button></span></label>
        <p class="gwd-bet-popup__hint" data-gwd-bet-input-hint></p>
        <div class="gwd-bet-popup__payment"><span>Thanh toán khi thắng đủ</span><strong data-gwd-bet-return>—</strong></div>
        <div class="gwd-bet-popup__amounts" role="group" aria-label="Chọn số tiền"><button type="button" data-gwd-bet-amount="100000">100 nghìn</button><button type="button" data-gwd-bet-add="10000">+10 nghìn</button><button type="button" data-gwd-bet-add="50000">+50 nghìn</button><button type="button" data-gwd-bet-amount="500000">500 nghìn</button></div>
        <div class="gwd-bet-popup__totals" aria-live="polite"><div><span>Tiền chịu rủi ro</span><strong data-gwd-bet-stake-total>—</strong></div><div><span>Lợi nhuận</span><strong data-gwd-bet-profit>—</strong></div></div>
        <div class="gwd-bet-popup__event"><p data-gwd-bet-league></p><p data-gwd-bet-home></p><p data-gwd-bet-away></p></div>
        <button type="button" class="gwd-bet-popup__back" data-gwd-bet-back>↪ Quay lại trận đấu</button>
        <button type="button" class="gwd-bet-popup__remove" data-gwd-bet-remove>Xóa lựa chọn</button>
      </div></div>`;
    root.append(dialog);
    resumeDrag = mountPopupDrag(dialog, () => trigger);
    dialog.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-gwd-bet-close]')) dialog!.close();
      const input = dialog!.querySelector<HTMLInputElement>('[data-gwd-bet-stake]')!;
      if (target?.closest('[data-gwd-bet-clear]')) {
        input.value = ''; notifyAmount(); input.focus();
      }
      if (target?.closest('[data-gwd-bet-remove]')) {
        input.value = ''; available = false; notifyAmount(); dialog!.close();
      }
      if (target?.closest('[data-gwd-bet-back]')) {
        dialog!.close();
        trigger?.closest('[data-gwd-odds-match-id]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      const collapse = target?.closest<HTMLButtonElement>('[data-gwd-bet-collapse]');
      if (collapse) {
        const body = dialog!.querySelector<HTMLElement>('[data-gwd-bet-body]')!;
        body.hidden = !body.hidden;
        collapse.setAttribute('aria-expanded', String(!body.hidden));
        collapse.setAttribute('aria-label', body.hidden ? 'Mở rộng' : 'Thu gọn');
        collapse.textContent = body.hidden ? '⌄' : '⌃';
      }
      const addition = target?.closest<HTMLElement>('[data-gwd-bet-add]');
      if (addition) {
        const current = Number(stakeDigits(input.value));
        input.value = formatStake(String((Number.isSafeInteger(current) && current > 0 ? current : 0) + Number(addition.dataset.gwdBetAdd)));
        notifyAmount();
      }
      const amount = target?.closest<HTMLElement>('[data-gwd-bet-amount]');
      if (amount) {
        input.value = formatStake(amount.dataset.gwdBetAmount || '');
        notifyAmount();
      }
      if (event.target === dialog) {
        const bounds = dialog!.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog!.close();
      }
    });
    const stakeInput = dialog.querySelector<HTMLInputElement>('[data-gwd-bet-stake]')!;
    stakeInput.addEventListener('input', (event) => {
      if ((event as InputEvent).isComposing) return;
      formatStakeInput(stakeInput);
      notifyAmount();
    });
    stakeInput.addEventListener('compositionend', () => {
      formatStakeInput(stakeInput);
      notifyAmount();
    });
    dialog.addEventListener('close', () => { if (trigger?.isConnected) trigger.focus({ preventScroll: true }); });
    return dialog;
  };
  const readCompany = () => {
    const company = root.querySelector<HTMLSelectElement>('[data-gwd-odds-company-select]');
    const active = root.querySelector<HTMLElement>('[data-gwd-odds-company-id].is-active');
    return {
      companyId: company?.value || active?.dataset.gwdOddsCompanyId || '',
      company: company?.selectedOptions[0]?.textContent?.trim() || active?.textContent?.trim() || '',
    };
  };
  const readSelection = (row: HTMLElement, node: HTMLElement, quote: typeof quotes[number]) => {
    const line = quote.line ? text(row, `data-gwd-odds-${quote.line}`) : '';
    let group = row.previousElementSibling;
    while (group && !group.hasAttribute('data-gwd-odds-league-id')) group = group.previousElementSibling;
    const statusId = row.dataset.gwdOddsStatusId || row.querySelector('[data-status-id]')?.getAttribute('data-status-id') || '';
    return {
      matchId: row.dataset.gwdOddsMatchId || '', ...readCompany(),
      home: text(row, 'data-gwd-home'), away: text(row, 'data-gwd-away'),
      'home-score': text(row, 'data-gwd-home-score') || '—', 'away-score': text(row, 'data-gwd-away-score') || '—',
      market: quote.label, marketKey: quote.prefix, side: quote.side, period: quote.period,
      line: line === '-' ? '' : line, selection: labels[quote.side], price: priceText(node),
      league: group ? text(group, 'data-gwd-group-title') : '', statusId,
      decimal: node.dataset.gwdOddsDecimal || '', format: normalizeDisplayOddsFormat(node.dataset.gwdOddsFormat),
      'format-label': ODDS_FORMAT_LABELS[normalizeDisplayOddsFormat(node.dataset.gwdOddsFormat)].replace(/^TL /, ''),
    };
  };
  const renderSelection = () => {
    Object.entries(selection).forEach(([key, value]) => fill(key, value));
    dialog!.querySelector<HTMLElement>('[data-gwd-bet-live]')!.hidden = !(Number(selection.statusId) >= 2 && Number(selection.statusId) <= 7);
    dialog!.querySelector<HTMLElement>('[data-gwd-bet-league]')!.hidden = !selection.league;
    // Native pre-match scores are '-'/'-'; custom templates use 'VS'/empty.
    const beforeKickoff = selection['home-score'] === 'VS'
      || (selection['home-score'] === '-' && selection['away-score'] === '-');
    dialog!.querySelector<HTMLElement>('[data-gwd-bet-vs]')!.hidden = !beforeKickoff;
    dialog!.querySelectorAll<HTMLElement>('[data-gwd-bet-home-score], [data-gwd-bet-away-score], [data-gwd-bet-score-separator]').forEach((node) => {
      node.hidden = beforeKickoff;
    });
    fill('price', available ? `@${selection.price}` : '—');
    const status = dialog!.querySelector<HTMLElement>('[data-gwd-bet-status]')!;
    status.hidden = available;
    status.textContent = available ? '' : 'Kèo hiện không khả dụng trong bảng đang xem.';
    dialog!.dataset.gwdBetAvailable = String(available);
    notifyAmount();
  };
  popupSyncers.set(root, () => {
    if (!dialog?.open) return;
    const quote = quotes.find(({ hook }) => hook === selectedHook);
    if (!quote) return;
    const previous = JSON.stringify([selection, available]);
    const rows = Array.from(root.querySelectorAll<HTMLElement>('[data-gwd-odds-match-id]'));
    const previousRow = trigger?.closest<HTMLElement>('[data-gwd-odds-match-id]');
    const row = rows.find((candidate) => candidate === previousRow && candidate.dataset.gwdOddsMatchId === selection.matchId)
      || rows.find((candidate) => candidate.dataset.gwdOddsMatchId === selection.matchId);
    const node = row?.querySelector<HTMLElement>(`[${selectedHook}]`);
    const company = readCompany();
    available = Boolean(row && node && validPrice(node) && company.companyId === selection.companyId);
    if (available && row && node) {
      selection = readSelection(row, node, quote);
      trigger = node;
    } else {
      // Preserve identity, but never expose a stale quote to calculation listeners.
      selection = {
        ...selection,
        company: company.companyId === selection.companyId ? company.company : selection.company,
        home: row ? text(row, 'data-gwd-home') : '—', away: row ? text(row, 'data-gwd-away') : '—',
        'home-score': row ? text(row, 'data-gwd-home-score') || '—' : '—',
        'away-score': row ? text(row, 'data-gwd-away-score') || '—' : '—',
        line: '', price: '', decimal: '', statusId: '', league: '', selection: labels[quote.side],
      };
    }
    if (previous !== JSON.stringify([selection, available])) renderSelection();
  });
  const open = (node: HTMLElement) => {
    const row = node.closest<HTMLElement>('[data-gwd-odds-match-id]');
    const quote = quotes.find(({ hook }) => node.hasAttribute(hook));
    if (!row || !quote || !validPrice(node)) return;
    selection = readSelection(row, node, quote);
    selectedHook = quote.hook; available = true;
    const popup = ensureDialog(); trigger = node;
    popup.querySelector<HTMLElement>('[data-gwd-bet-body]')!.hidden = false;
    const collapse = popup.querySelector<HTMLButtonElement>('[data-gwd-bet-collapse]')!;
    collapse.setAttribute('aria-expanded', 'true'); collapse.setAttribute('aria-label', 'Thu gọn'); collapse.textContent = '⌃';
    popup.querySelector<HTMLInputElement>('[data-gwd-bet-stake]')!.value = '100.000';
    if (!popup.open) popup.showModal();
    renderSelection();
    resumeDrag?.();
  };
  root.addEventListener('click', (event) => {
    const node = event.target instanceof Element ? event.target.closest<HTMLElement>(selector) : null;
    if (node) { event.preventDefault(); open(node); }
  });
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const node = event.target instanceof Element ? event.target.closest<HTMLElement>(selector) : null;
    if (node) { event.preventDefault(); open(node); }
  });
};
